import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex, Link } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { format, compareAsc } from 'date-fns';
import { capitalize } from 'lodash';
import { useLocation } from 'react-router-dom';
import { RRule } from 'rrule';
import { oneOneOneReactQueryKeys } from 'screens/oneOnOneRabbit/InstancePage/constants/oneOnOneReactQueryKeys';
import { useOneOnOneGeneralSettingsDetails } from 'screens/oneOnOneRabbit/SettingsPage/hooks/dataHooks';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { oneOnOneLandingPageReactQueryKeys } from 'screens/oneOnOneRabbit/landingPage/constants/oneOnOneReactQueryKeys';
import { check1on1TranscriptionEnabled } from 'utils/helpers/featureFlags';

import { CloseCircle, Ellipsis, Pen, Archive, Info, CheckOutlined, Check } from 'assets/assetsV1/iconComponents';

import { getQueryParams } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import {
  sendArchivedOneOnOneInstanceEvent,
  sendCancelledOneOnOneInstanceEvent,
  sendCompletedOneonone,
} from 'constants/mixpanelHelper';

import { BUTTON_VARIANT, Button } from 'uiToolkitV1/Button';
import { Menu } from 'uiToolkitV1/Menu';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyLarge, TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { completeMeetingApi, markAsCompleteApi } from '../../apis';
import { ArchiveMeetingModal } from '../../common/modals/ArchiveMeetingModal';
import { CancelMeetingModal } from '../../common/modals/CancelMeetingModal';
import { UnArchiveMeetingModal } from '../../common/modals/UnArchiveMeetingModal';
import { useOneOnOneInstanceDetails } from '../../hooks/dataHooks';
import { archiveOneOnOneMeeting } from '../../services';
import MeetingsDetailsExpanded from './MeetingsDetailsExpanded';

const MeetingDetails = () => {
  const { masterId, instanceId } = useOneOnOnePageParams();
  const { search: queryString } = useLocation();
  const { isReschedule = false } = getQueryParams(queryString);
  const {
    startTimeTs,
    meetingLink,
    recurrenceRule,
    completionStatus,
    completionTime,
    latestInstanceId,
    isArchived,
    statusChangedBy,
    statusChangeSource,
  } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const { data: settingsData, isLoading: isLoadingSettings } = useOneOnOneGeneralSettingsDetails();

  const isLatestInstance = latestInstanceId === instanceId;

  const startTime = new Date(startTimeTs * 1000);

  const queryClient = useQueryClient();

  const toast = useToast();

  const [isExpanded, setIsExpanded] = useState(isReschedule);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

  const [source, setSource] = useState('');

  const [isUnarchiveModalOpen, setIsUnarchiveModalOpen] = useState(false);
  const [isMeetingStarted, setMeetingStarted] = useState(false);
  const meetingStatusIntervalIdRef = useRef(null);

  const refetchLandingPageQueries = () => {
    queryClient.invalidateQueries([oneOnOneLandingPageReactQueryKeys.oneOnOneLandingPageDetails]);
    queryClient.invalidateQueries([oneOnOneLandingPageReactQueryKeys.oneOnOneActiveArchiveCount]);
  };

  useEffect(() => {
    const isMeetingStartedValue = compareAsc(startTime, new Date()) <= 0;
    setMeetingStarted(isMeetingStartedValue);

    if (isMeetingStartedValue === false && meetingStatusIntervalIdRef.current === null) {
      meetingStatusIntervalIdRef.current = setInterval(() => {
        const isMeetingStartedNow = compareAsc(startTime, new Date()) <= 0;

        if (isMeetingStartedNow) {
          setMeetingStarted(isMeetingStartedNow);
          clearInterval(meetingStatusIntervalIdRef.current);
          meetingStatusIntervalIdRef.current = null;
        }
      }, 10000);
    }

    return () => {
      clearInterval(meetingStatusIntervalIdRef.current);
      meetingStatusIntervalIdRef.current = null;
    };
  }, [startTime]);

  const markAsComplete = async () => {
    try {
      await markAsCompleteApi({
        masterId,
        instanceId,
      });
      refetchLandingPageQueries();
      sendCompletedOneonone();
      queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneInstanceDetails]);
      toast({
        title: `Your 1:1 is completed successfully`,
        status: 'success',
      });
    } catch (error) {
      console.error('Error while completing 1:1 : ', error);
      toast({
        title: error.message || 'Unable to mark 1:1 instance complete',
        status: 'error',
      });
    }
  };

  const getStatusText = () => {
    const statusText = completionStatus === 'CANCELLED' ? 'Canceled' : 'Completed';
    const userName = statusChangedBy?.displayName || 'NA';
    const completionTimeDate = completionTime ? format(completionTime, 'do MMM yyyy') : 'NA';
    const completionTimeHours = completionTime ? format(completionTime, 'h:mm a') : 'NA';

    switch (statusChangeSource) {
      case 'USER':
        return `${statusText} by ${userName} on ${completionTimeDate} at ${completionTimeHours}`;
      case 'CANCEL_UI':
        return `${statusText} by ${userName} on ${completionTimeDate} at ${completionTimeHours}`;
      case 'COMPLETE_UI':
        return `${statusText} by ${userName} on ${completionTimeDate} at ${completionTimeHours}`;
      case 'JOB':
        return `${statusText} automatically on ${completionTimeDate} at ${completionTimeHours} due to agenda, initiatives or notes being ${
          completionStatus === 'CANCELLED' ? 'missing' : 'added'
        }`;
      case 'CANCEL_GOOGLE':
        return `Canceled from calendar on ${completionTimeDate} at ${completionTimeHours}`;
      case 'CANCEL_OUTLOOK':
        return `Canceled from calendar on ${completionTimeDate} at ${completionTimeHours}`;
      case 'API_FIX':
        return '';
      case 'INITIAL':
        return '';
      default:
        return '';
    }
  };

  const menuOptions = useMemo(
    () => [
      {
        label: 'Edit 1:1 Schedule',
        onClick: () => {
          setSource('kebab menu');
          setIsExpanded(true);
        },
        Icon: Pen,
        enabled: isLatestInstance,
      },
      {
        label: 'Cancel this meeting',
        onClick: () => {
          setIsCancelModalOpen(true);
        },
        Icon: CloseCircle,
        enabled: completionStatus !== 'CANCELLED' && isLatestInstance,
      },
      // {
      //   label: 'Archive this 1:1',
      //   onClick: () => {
      //     setIsArchiveModalOpen(true);
      //   },
      //   Icon: Archive,
      //   enabled: !isArchived,
      // },
      {
        label: 'Unarchive this 1:1',
        onClick: () => {
          setIsUnarchiveModalOpen(true);
        },
        Icon: Archive,
        enabled: isArchived,
      },
      {
        label: 'Mark as complete',
        onClick: async () => {
          try {
            await completeMeetingApi({
              masterId,
              instanceId,
              status: 'COMPLETED',
            });
            sendCompletedOneonone();
            refetchLandingPageQueries();
            queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneInstanceDetails]);
            toast({
              title: `Your 1:1 is completed successfully`,
              status: 'success',
            });
          } catch (error) {
            console.error('Error while completing 1:1 : ', error);
          }
        },
        Icon: CheckOutlined,
        enabled: completionStatus === 'CANCELLED',
      },
      {
        label: 'Mark as canceled',
        onClick: async () => {
          try {
            await completeMeetingApi({
              masterId,
              instanceId,
              status: 'CANCELLED',
            });
            refetchLandingPageQueries();
            queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneInstanceDetails]);
            toast({
              title: `Your 1:1 is canceled successfully`,
              status: 'success',
            });
          } catch (error) {
            console.error('Error while canceling 1:1 : ', error);
          }
        },
        Icon: CheckOutlined,
        enabled: completionStatus === 'COMPLETED',
      },
    ],
    [completionStatus, isLatestInstance, isArchived]
  );

  const validMenuOptions = useMemo(() => menuOptions.filter((option) => option.enabled), [menuOptions]);

  const onSave = () => {
    setIsExpanded(false);
  };

  const rRule = RRule?.fromString(recurrenceRule)?.toText();

  const dayCapitalizedRRuleString = useMemo(() => {
    if (!rRule) {
      return '';
    }
    const splittedRRule = rRule.split(' ');
    const day = splittedRRule[splittedRRule.length - 1];
    const rest = splittedRRule.slice(0, splittedRRule.length - 1);
    return `${capitalize(rest.join(' '))} ${capitalize(day)}`;
  }, [rRule]);

  const onArchive = () => {
    try {
      setIsArchiveModalOpen(false);

      refetchLandingPageQueries();
      queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneInstanceDetails]);
      toast({
        title: `Your 1:1 is archived successfully`,
        status: 'success',
      });
    } catch (error) {
      console.error('Error while archiving 1:1 : ', error);
    }
  };

  const onUnarchive = async () => {
    setIsUnarchiveModalOpen(false);
    try {
      setIsUnarchiveModalOpen(false);
      await archiveOneOnOneMeeting({
        masterId,
        isArchive: false,
      });
      refetchLandingPageQueries();
      queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneInstanceDetails]);
      toast({
        title: `Your 1:1 is unarchived successfully`,
        status: 'success',
      });
    } catch (error) {
      console.error('Error while unarchiving 1:1 : ', error);
    }
  };

  if (isExpanded) {
    return <MeetingsDetailsExpanded source={source} onCancel={() => setIsExpanded(false)} onSave={onSave} />;
  }

  return (
    <>
      <PaperWithShadow width="100%" display="flex" p="8px 26px" background="#FDFDFD" h="84px">
        <Flex w="100%" alignItems="center">
          <Flex flexDir="column" mr="auto" w={isMobileView() ? 'auto' : '40%'}>
            <TextBodyLarge
              bold
              sx={{
                borderBottom: '1px dashed #999999',
                paddingBottom: '10px',
              }}
              onClick={() => {
                if (!isLatestInstance) {
                  return;
                }
                setIsExpanded(true);
                setSource('date heading');
              }}
              cursor={isLatestInstance ? 'pointer' : 'default'}
            >
              1:1 on {format(startTime, 'MMM dd yyyy')} at {format(startTime, 'h:mm a')}
            </TextBodyLarge>
            {!isMobileView() && (
              <TextLabel color="text.g3" mt="4px">
                {dayCapitalizedRRuleString ?? ''}
              </TextLabel>
            )}
          </Flex>
          {completionStatus === 'YET_TO_START' && meetingLink && (
            <Flex>
              <Link href={meetingLink} isExternal>
                <Button variant="tertiary">Join Meeting</Button>
              </Link>

              {check1on1TranscriptionEnabled() && meetingLink && settingsData?.includeTranscriptionBot && (
                <Tooltip
                  placement="top"
                  label="A meeting bot will be joining you in this meeting to provide a summary along with any
              actionable from that meeting. The summary is confidential and accessible only to the
              participants of the call."
                >
                  <Box>
                    <Info
                      m="9px 8px 0px -12px"
                      boxSize="24px"
                      sx={{
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Tooltip>
              )}
            </Flex>
          )}

          {completionStatus === 'CANCELLED' && (
            <Tooltip label={getStatusText()} shouldWrapChildren placement="bottom">
              <TextBodyMedium mr="12px" color="#C62C02" ml="auto">
                Meeting Canceled
              </TextBodyMedium>
            </Tooltip>
          )}
          {completionStatus === 'COMPLETED' && (
            <Tooltip label={getStatusText()} shouldWrapChildren placement="bottom">
              <TextBodyMedium mr="12px" color="#1A5724" ml="auto">
                Meeting Completed
              </TextBodyMedium>
            </Tooltip>
          )}
          {completionStatus === 'YET_TO_START' && isMeetingStarted && !isMobileView() && !isLoadingSettings && (
            <Button variant={BUTTON_VARIANT.outlined} StartIcon={Check} onClick={markAsComplete}>
              Mark as complete
            </Button>
          )}
          <Menu
            sx={{
              '&.chakra-menu__menu-button': {
                padding: '0px',
                width: '37px',
              },
            }}
            MenuIcon={Ellipsis}
            menuList={validMenuOptions}
          />
        </Flex>
      </PaperWithShadow>
      <CancelMeetingModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onSave={() => {
          sendCancelledOneOnOneInstanceEvent({
            eventProperties: {
              instance_id: instanceId,
            },
          });
          queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneInstanceDetails]);
          setIsCancelModalOpen(false);
        }}
      />
      <ArchiveMeetingModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        onSave={() => {
          sendArchivedOneOnOneInstanceEvent({
            eventProperties: {
              instance_id: instanceId,
              source: ' 1:1 instance page',
            },
          });
          onArchive();
        }}
      />
      <UnArchiveMeetingModal
        isOpen={isUnarchiveModalOpen}
        onClose={() => setIsUnarchiveModalOpen(false)}
        onSave={onUnarchive}
      />
    </>
  );
};

export { MeetingDetails };
